import { Button } from "primereact/button";
import { SegmentedProgressBar } from "sj-ui-toolkit/src/atlas/components/SegmentedProgressBar";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import type { ProficiencyDistribution } from "@/constants/assessmentsReport.constants";

interface ProficiencyFilterProps {
  proficiencyDistribution: ProficiencyDistribution;
  activeFilterKey: string | null;
  onFilterClick: (key: string) => void;
  onClearFilter: () => void;
  hiddenKeys?: string[];
}

const ALL_LEVELS = [
  { key: "foundational", label: "Foundational" },
  { key: "intermediate", label: "Intermediate" },
  { key: "advanced", label: "Advanced" },
  { key: "inProgress", label: "In progress" },
  { key: "notStarted", label: "Not started" },
] as const;

const DEFAULT_BG: Record<string, string> = {
  foundational: themeClasses.bgIndigoDataviz1,
  intermediate: themeClasses.bgIndigoDataviz2,
  advanced: themeClasses.bgIndigoDataviz3,
  inProgress: themeClasses.bgIndigoDataviz4,
  notStarted: themeClasses.bgIndigoDataviz5,
};

export default function ProficiencyFilter({
  proficiencyDistribution,
  activeFilterKey,
  onFilterClick,
  onClearFilter,
  hiddenKeys = [],
}: ProficiencyFilterProps) {
  const levels = ALL_LEVELS.filter(l => !hiddenKeys.includes(l.key));

  const segments = levels.map(l => ({
    key: l.key,
    value: proficiencyDistribution[l.key as keyof ProficiencyDistribution],
    label: l.label,
  }));

  const segmentColors = Object.fromEntries(levels.map(l => [l.key, DEFAULT_BG[l.key] ?? ""]));

  const getButtonProps = (key: string) => {
    if (activeFilterKey === key) {
      return {
        severity: "contrast" as const,
        className: `${themeClasses.bgIndigoDataviz2} ${themeClasses.textSurface0}`,
      };
    }
    if (activeFilterKey !== null) {
      return { severity: "secondary" as const };
    }
    const defaults: Record<string, { severity: "contrast" | "secondary"; className?: string }> = {
      foundational: {
        severity: "contrast",
        className: `${themeClasses.bgIndigoDataviz1} ${themeClasses.textSurface0}`,
      },
      intermediate: {
        severity: "contrast",
        className: `${themeClasses.bgIndigoDataviz2} ${themeClasses.textSurface0}`,
      },
      advanced: {
        severity: "contrast",
        className: `${themeClasses.bgIndigoDataviz3} ${themeClasses.textSurface950}`,
      },
      inProgress: {
        severity: "contrast",
        className: `${themeClasses.bgIndigoDataviz4} ${themeClasses.textSurface0}`,
      },
      notStarted: { severity: "secondary" },
    };
    return defaults[key] ?? { severity: "secondary" as const };
  };

  return (
    <div className="mb-12">
      <h3 className="text-base font-semibold mb-1">Assessment proficiency levels</h3>
      <p className={`body-sm ${themeClasses.textSecondary} mb-3`}>
        Click to filter all stats by level
      </p>

      <SegmentedProgressBar
        segments={segments}
        segmentColors={segmentColors}
        activeSegmentKey={activeFilterKey ?? undefined}
        inactiveColor={themeClasses.bgGray400}
        activeColor={themeClasses.bgIndigoDataviz2}
        className="mb-3.5"
      />

      <div className="flex gap-3.5">
        {levels.map(level => (
          <Button
            key={level.key}
            label={`${level.label} ${proficiencyDistribution[level.key as keyof ProficiencyDistribution]}%`}
            size="small"
            {...getButtonProps(level.key)}
            onClick={() => onFilterClick(level.key)}
          />
        ))}

        {activeFilterKey !== null && (
          <Button
            icon="pi pi-filter-slash"
            severity="secondary"
            onClick={onClearFilter}
            tooltip="Clear filter"
            tooltipOptions={{ position: "top" }}
          />
        )}
      </div>
    </div>
  );
}
